import { sortPartialDate } from 'neuro-utils';
import { sortAlphaObject } from 'Utility/string';
import { isString } from 'Utility/typeGuards';
import { uniq } from 'Utility/ramdaReplacement';
import InfoPopover from 'Components/InfoPopover';
import { examination } from 'neuro-schemas';

/**
 * Map field names so their values can be used as headers
 * @param {IDocumentWithTypeV2<'examination'>[]} docs - All documents
 * @param {'date'|'name'} field - Field name to make headers by
 * @returns {[]} Field value array
 */
export const mapFieldNamesToArray = (docs: IDocumentWithTypeV2<'examination'>[], field: 'date' | 'codeName'): any[] =>
  docs.length > 0 ? uniq(docs.map(d => d[field])) : [];

/**
 * Sort documents under the headers based on header sorting (types alphabetical ascending, dates descending)
 * @param {IDocumentWithTypeV2<'examination'>[]} docs - Documents to sort
 * @param {'date'|'codeName'} field - Field name to sort by
 * @returns {IDocumentWithTypeV2<'examination'>[]} Sorted documents
 */
export const sortDocuments = (
  docs: IDocumentWithTypeV2<'examination'>[],
  field: TSortableFields,
  direction: TSortDirection,
): IDocumentWithTypeV2<'examination'>[] => {
  docs.sort((n1, n2) => {
    if (field === 'codeName') {
      return sortAlphaObject(field)({ [field]: n1[field] ?? '' }, { [field]: n2[field] ?? '' });
    }

    return sortPartialDate(n1.date, n2.date);
  });
  return direction === 'descending' ? docs.reverse() : docs;
};

export type TSortDirection = 'ascending' | 'descending';

/**
 * Filter documents by text
 * @param  {string[]} texts - Text array to filter by
 * @param  {string[]} fields - By which fields the documents are filtered
 * @param  {IDocumentWithTypeV2<'examination'>[]} documents - Documents
 * @returns {IDocumentWithTypeV2<'examination'>[]} Filtered documents
 */
export const filterByTexts = (
  texts: string[],
  fields: SafeExclude<keyof IExamination, 'date' | 'value'>[],
  documents: IDocumentWithTypeV2<'examination'>[],
): IDocumentWithTypeV2<'examination'>[] => {
  const matchSome = (d: IDocumentWithTypeV2<'examination'>) => fields.some(s => d[s] && texts.includes(d[s]));

  return documents.filter(d => (texts.length > 0 ? matchSome(d) : true));
};

/**
 * Cumulate autocomplete search options from searched documents.
 * @param documents Documents to parse the options from.
 * @param fields Fields (properties) of the doc that are included.
 * @returns An array containing options for an autocomplete input field.
 */
export const cumulateOptions = (
  documents: IDocumentWithTypeV2<'examination'>[],
  fields: Array<keyof IDocumentWithTypeV2<'examination'>>,
): string[] => {
  let opts: string[] = [];
  fields.forEach(f => {
    documents.forEach(d => {
      const value = d[f];
      // allow only string values
      if (!isString(value)) return;
      else if (!opts.includes(value.toLowerCase()) && !opts.includes(value)) opts = opts.concat(value);
    });
  });
  return opts;
};

export type TSortableFields = keyof IFieldNames;

interface IFieldNames {
  date: string;
  codeName: string;
}

export const localizeCodenames = (option: string, fm: (opt: string) => string) => {
  if (option === 'LRP4_antibody' || option === 'ryanodine_antibody') {
    // Mgravis localizations
    return fm('examination.opts.' + option);
  } else return `${option}`;
};

const { abnormalFlagsWithDescription } = examination.config;

export const AbnormalFlagsDisplay = ({
  abnormalFlags,
  fm,
  info,
}: {
  abnormalFlags: IDocumentWithTypeV2<'examination'>['abnormalFlags'];
  fm: (id: string) => string;
  info?: boolean;
}) => {
  const code = abnormalFlags;
  if (!code) return null;

  const description = abnormalFlagsWithDescription.includes(abnormalFlags.toUpperCase())
    ? `${fm(`examination.abnormalFlagsDescription.` + abnormalFlags)}`
    : null;

  const InfoElement = description && info && <InfoPopover color="primary" size="2.4rem" text={<>{description}</>} />;
  return (
    <span style={{ display: 'inline-flex', verticalAlign: 'center' }}>
      {code}
      {!info && description ? ` - ${description}` : null}
      {info && description ? (
        <span style={{ marginLeft: '1rem', display: 'inline-flex', verticalAlign: 'middle' }}>{InfoElement}</span>
      ) : null}
    </span>
  );
};
